import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SidebarLayout from "../components/sidebarLayout";
import { createSetup, deleteSetup, getAllSetups, updateSetup, uploadSetupImage } from "../APIs/SetupsAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { useTranslation } from "react-i18next";

const emptyForm = {
  id: null,
  name: "",
  description: "",
  active: true,
  imagePaths: [],
};

function imageSrc(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith("/") ? path : `/${path}`;
}

function SetupImageCarousel({ setup, t }) {
  const images = Array.isArray(setup.imagePaths) ? setup.imagePaths.filter(Boolean) : [];
  const [index, setIndex] = useState(0);
  const safeIndex = images.length ? Math.min(index, images.length - 1) : 0;

  useEffect(() => {
    setIndex(0);
  }, [setup.id, images.length]);

  if (!images.length) {
    return (
      <Box sx={{ height: 150, display: "grid", placeItems: "center", bgcolor: "#fff7ed", color: "#d97706", fontWeight: 900 }}>
        {t("setups.noPhoto")}
      </Box>
    );
  }

  const goTo = (nextIndex) => {
    setIndex((nextIndex + images.length) % images.length);
  };

  return (
    <Box sx={{ position: "relative", height: 150, bgcolor: "#111827" }}>
      <CardMedia component="img" height="150" image={imageSrc(images[safeIndex])} alt={setup.name} sx={{ objectFit: "cover" }} />
      {images.length > 1 && (
        <>
          <IconButton
            size="small"
            onClick={() => goTo(safeIndex - 1)}
            sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,.88)", "&:hover": { bgcolor: "white" } }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => goTo(safeIndex + 1)}
            sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,.88)", "&:hover": { bgcolor: "white" } }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Chip
            size="small"
            label={`${safeIndex + 1}/${images.length}`}
            sx={{ position: "absolute", right: 8, bottom: 8, bgcolor: "rgba(17,24,39,.82)", color: "white", fontWeight: 800 }}
          />
        </>
      )}
    </Box>
  );
}

export default function GestioneSetupsPage() {
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [setups, setSetups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadSetups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllSetups(token, tenant);
      setSetups(Array.isArray(data) ? data : []);
    } catch (err) {
      showSnackbar(err.message || t("setups.loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, tenant, token, t]);

  useEffect(() => { loadSetups(); }, [loadSetups]);

  const filteredSetups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return setups;
    return setups.filter((item) => `${item.name || ""} ${item.description || ""}`.toLowerCase().includes(q));
  }, [search, setups]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (setup) => {
    setForm({
      id: setup.id,
      name: setup.name || "",
      description: setup.description || "",
      active: setup.active !== false,
      imagePaths: Array.isArray(setup.imagePaths) ? setup.imagePaths : [],
    });
    setDialogOpen(true);
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const result = await uploadSetupImage(file, token, tenant);
        if (result?.path) uploaded.push(result.path);
      }
      setForm((prev) => ({ ...prev, imagePaths: [...prev.imagePaths, ...uploaded] }));
    } catch (err) {
      showSnackbar(err.message || t("setups.uploadError"), "error");
    }
  };

  const removeImage = (path) => {
    setForm((prev) => ({ ...prev, imagePaths: prev.imagePaths.filter((item) => item !== path) }));
  };

  const saveSetup = async () => {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      active: form.active,
      imagePaths: form.imagePaths,
    };
    if (!payload.name) {
      showSnackbar(t("setups.validation.nameRequired"), "error");
      return;
    }
    setLoading(true);
    try {
      if (form.id) await updateSetup(form.id, payload, token, tenant);
      else await createSetup(payload, token, tenant);
      showSnackbar(t("setups.saved"), "success");
      setDialogOpen(false);
      await loadSetups();
    } catch (err) {
      showSnackbar(err.message || t("setups.saveError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (setup) => {
    if (!window.confirm(t("setups.deleteConfirm", { name: setup.name }))) return;
    setLoading(true);
    try {
      await deleteSetup(setup.id, token, tenant);
      showSnackbar(t("setups.deleted"), "success");
      await loadSetups();
    } catch (err) {
      showSnackbar(err.message || t("setups.deleteError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (tenantType !== "SALE_CONGRESSI") {
    return (
      <SidebarLayout className="admin-responsive-page">
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={900}>{t("setups.conferenceOnlyTitle")}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {t("setups.conferenceOnlySubtitle")}
            </Typography>
          </Paper>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
          <Box>
            <Typography variant="h4" fontWeight={900}>{t("setups.title")}</Typography>
            <Typography color="text.secondary">{t("setups.subtitle")}</Typography>
          </Box>
          <Button variant="contained" onClick={openCreate}>{t("setups.newSetup")}</Button>
        </Stack>

        <Paper sx={{ mt: 3, p: 2, borderRadius: 3 }}>
          <TextField fullWidth label={t("setups.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
        </Paper>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {filteredSetups.map((setup) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={setup.id}>
              <Card sx={{ height: "100%", borderRadius: 3, overflow: "hidden" }}>
                <SetupImageCarousel setup={setup} t={t} />
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={900}>{setup.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 38 }}>{setup.description || "-"}</Typography>
                    </Box>
                    <Chip size="small" color={setup.active ? "success" : "default"} label={setup.active ? t("common.status.active") : t("common.status.inactive")} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{t("setups.photoCount", { count: setup.imagePaths?.length || 0 })}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(setup)}>{t("common.actions.edit")}</Button>
                    <IconButton size="small" color="error" onClick={() => handleDelete(setup)}><DeleteIcon /></IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {!filteredSetups.length && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                <Typography fontWeight={800}>{loading ? t("common.loading") : t("setups.empty")}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{form.id ? t("setups.editSetup") : t("setups.newSetup")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField label={t("setups.setupName")} value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required />
              <TextField multiline minRows={4} label={t("setups.description")} value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch checked={form.active} onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))} />
                <Typography fontWeight={700}>{t("setups.setupActive")}</Typography>
              </Stack>
              <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}>
                {t("setups.uploadImages")}
                <input hidden multiple type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }} />
              </Button>
              <Grid container spacing={1}>
                {form.imagePaths.map((path) => (
                  <Grid item xs={6} sm={4} md={3} key={path}>
                    <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", border: "1px solid #eee" }}>
                      <Box component="img" src={imageSrc(path)} alt={t("setups.imageAlt")} sx={{ width: "100%", height: 110, objectFit: "cover", display: "block" }} />
                      <IconButton size="small" color="error" onClick={() => removeImage(path)} sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,.9)" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("common.actions.cancel")}</Button>
            <Button variant="contained" disabled={loading} onClick={saveSetup}>{t("common.actions.save")}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
